import { connectSipRecoil, environmentRecoil } from '@/recoil/atoms/MobileRecoil/MobileRecoil';
import { TelHyphenWithStarHash } from '@/shared/util/format/phoneNumberFormatUtil';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { FaPhone } from 'react-icons/fa';
import { FaDeleteLeft } from 'react-icons/fa6';
import { TiArrowSortedDown, TiArrowSortedUp } from 'react-icons/ti';
import { useRecoilState, useRecoilValue } from 'recoil';
import WebKeypad from './sip-components/WebKeypad';
import {
  callIsVisibleRecoil,
  callStatusRecoil,
  incomingCallInfoRecoil,
  phoneNumberRecoil,
} from './sip-recoil/sipRecoil';
import { endCall, getCurrentSession, makeCall } from './siphook/sipService';
import useRingtone from './siphook/useRingtone';
import useSipClient from './siphook/useSipClient';
import * as S from './sipstyles/SipWebClient.style';
import { getSipConnectionInfo } from './sipApi/sipApi';
import { useLocation } from 'react-router-dom';

interface SipWebClientProps {
  authorizationToken: boolean;
}

const SipWebClient = ({ authorizationToken }: SipWebClientProps) => {
  const location = useLocation(); // React Router의 현재 경로 감지
  const [callTime, setCallTime] = useState(0); // 통화 시간 (초)
  const [phoneNumber, setPhoneNumber] = useRecoilState(phoneNumberRecoil); // 입력한 전화번호
  const [debouncePhoneNumber, setDebouncePhoneNumber] = useState<string>('');
  const [callStatus, setCallStatus] = useRecoilState(callStatusRecoil); // idle, dialing, connected, ended, ringing
  const [sipStatus, setSipStatus] = useState<'connected' | 'disconnected' | null>(null);
  const [incomingCallInfo, setIncomingCallInfo] = useRecoilState(incomingCallInfoRecoil); // 수신 전화 정보
  const [callIsVisibleS, setCallIsVisibleS] = useRecoilState(callIsVisibleRecoil);
  const [isMobileSipClient, setIsMobileSipClient] = useState(false);

  const phoneInputRef = useRef<HTMLInputElement>(null); // PhoneDisplay에 대한 ref 추가

  const environmentS = useRecoilValue(environmentRecoil);

  const { start, stop } = useSipClient({ setSipStatus, setIncomingCallInfo, setCallStatus }); // 훅 사용
  useRingtone({ callStatus, audioSrc: '/file/audioFile/incoming-call-bell.mp3' }); // 전화 걸려왔을 때 벨소리 Hook

  const pcEnvironment = environmentS === 'pc'; // 웹, 모바일 키패드 인풋의 사용성을 위한 변수

  // TODO - SD ==========>
  const connectSipS = useRecoilValue(connectSipRecoil);
  // TODO - SD <==========

  const { data, isLoading, isError } = useQuery(['/sip/connection-info'], getSipConnectionInfo, {
    enabled: connectSipS,
  });

  // ---------------------------------------------------------------

  useEffect(() => {
    setIsMobileSipClient(location.pathname.includes('mobile/sipClient'));
  }, [location.pathname]); // 👈 `location.pathname`이 변경될 때 실행

  useEffect(() => {
    if (!authorizationToken) return; // 토큰이 없으면 실행하지 않음

    const handleNetworkRestore = () => {
      if (!sipStatus && data && !isLoading && !isError) {
        handleStart(); // 네트워크 복구 시 handleStart 호출
      }
    };

    // 네트워크 복구 이벤트 리스너 추가
    window.addEventListener('online', handleNetworkRestore);

    // 초기 실행 (네트워크가 이미 연결된 상태일 때)
    if (navigator.onLine) {
      handleNetworkRestore();
    }

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('online', handleNetworkRestore);
    };
  }, [sipStatus, authorizationToken]);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (callStatus === 'connected') {
      timer = setInterval(() => {
        setCallTime((prev) => prev + 1);
      }, 1000);
    } else {
      if (timer) clearInterval(timer); // 타이머 초기화
      setCallTime(0);
    }

    return () => {
      if (timer) clearInterval(timer); // cleanup
    };
  }, [callStatus]);

  const handleStart = () => {
    start(data);
  };

  // 통화 시간 포맷
  const formatCallTime = () => {
    const minutes = Math.floor(callTime / 60);
    const seconds = callTime % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleKeypad = (key: string) => {
    setPhoneNumber((prev) => prev + key);
  };

  // 통화 종료 이벤트시 2초간 대기 후 대기상태로 변경
  useEffect(() => {
    if (callStatus === 'ended') {
      // 1초 후 대기 상태로 변경 -> 종료하면 해당 상태값 변경으로 바꾸면 됌
      setTimeout(() => {
        setCallStatus('idle');
        setIncomingCallInfo(null); // 수신인 정보 초기화
        setCallIsVisibleS(false);
      }, 2000);
    }
  }, [callStatus]);

  // 전화 걸기
  const handleDial = () => {
    if (phoneNumber) {
      makeCall(phoneNumber, setCallStatus);
      setDebouncePhoneNumber('');
    }
  };

  // 전화 받기
  const handleAcceptCall = () => {
    const session = getCurrentSession();

    if (session) {
      session.answer({
        mediaConstraints: { audio: true, video: false }, // 오디오만 활성화
      });

      const peerConnection = session.connection;
      const remoteStream = new MediaStream();

      peerConnection.ontrack = (event: any) => {
        event.streams[0].getTracks().forEach((track: any) => {
          remoteStream.addTrack(track);
        });

        const remoteAudio = document.getElementById('remoteAudio') as HTMLAudioElement;
        if (remoteAudio) {
          remoteAudio.srcObject = remoteStream;
          remoteAudio.play().catch((err) => console.error('오디오 재생 오류:', err));
        }
      };

      setCallStatus('connected');
    }
    // 통화 연결 처리 로직 추가
  };

  // 전화 거절
  const handleRejectCall = () => {
    const session = getCurrentSession();
    if (session) {
      session.terminate();
      setCallStatus('ended');
    }
    // 통화 거절 처리 로직 추가
  };

  // 통화 종료
  const handleEndCall = () => {
    setCallStatus('ended');
    endCall();
    setPhoneNumber('');
  };

  const getStatusText = () => {
    if (callStatus === 'connected') {
      return incomingCallInfo ? '통화 중' : '전화 연결됨';
    }

    switch (callStatus) {
      // case 'idle':
      //   return '대기 중';
      case 'dialing':
        return '전화 중...';
      case 'ringing':
        return '수신 전화';
      case 'ended':
        return '통화 종료됨';
      default:
        return '';
    }
  };

  // 다이얼 번호 지우기
  const handleBackspace = () => {
    setPhoneNumber((prev) => prev.slice(0, -1));
  };

  // 다이얼 번호 전체 지우기
  const handleBackspaceAll = () => {
    setPhoneNumber('');
  };

  useEffect(() => {
    if (callStatus !== 'idle') {
      setCallIsVisibleS(true);
    }
  }, [callStatus]);

  return (
    <div style={{ opacity: isMobileSipClient ? '0' : '1' }}>
      <S.PageContainer
        $isVisible={callIsVisibleS}
        $pcEnvironment={pcEnvironment}
        onClick={() => {
          // PageContainer 클릭 시 PhoneDisplay에 포커스
          if (pcEnvironment) {
            phoneInputRef.current?.focus();
          }
        }}
      >
        {/* <S.SipButton disconnected={sipStatus === 'disconnected'} onClick={handleStart}>
        015 번호 연결
      </S.SipButton> */}

        <S.Container>
          <S.PhoneContainer>
            {getStatusText() ? (
              <>
                <S.StatusDisplay>
                  <S.CallTime>
                    {callStatus === 'ended' && '통화종료...'}
                    {callStatus === 'ringing' && (
                      <S.RingingWrap>
                        <FaPhone />
                        <span>수신중...</span>
                      </S.RingingWrap>
                    )}
                    {callStatus !== 'ended' && callStatus !== 'ringing' && formatCallTime()}
                  </S.CallTime>
                  <h2>
                    {(callStatus === 'dialing' || (!incomingCallInfo && callStatus === 'connected')) &&
                      TelHyphenWithStarHash(phoneNumber)}
                    {(callStatus === 'ringing' || (incomingCallInfo && callStatus === 'connected')) &&
                      (incomingCallInfo?.caller ? TelHyphenWithStarHash(incomingCallInfo.caller) : '알 수 없음')}
                  </h2>
                </S.StatusDisplay>
              </>
            ) : (
              <>
                <S.PhoneDisplay
                  ref={phoneInputRef}
                  placeholder="010-0000-0000"
                  value={TelHyphenWithStarHash(phoneNumber)}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleDial();
                    }
                  }}
                  autoFocus={pcEnvironment}
                  readOnly={!pcEnvironment}
                />
                <S.BackspaceButton
                  phoneNumber={phoneNumber !== '' && callStatus === 'idle'}
                  onClick={handleBackspace}
                  disabled={phoneNumber === '' || callStatus !== 'idle'}
                >
                  <FaDeleteLeft />
                </S.BackspaceButton>
              </>
            )}
          </S.PhoneContainer>
          <WebKeypad
            phoneNumber={phoneNumber}
            callStatus={callStatus}
            onKeypad={handleKeypad}
            onDial={handleDial}
            onBackspace={handleBackspace}
            onBackspaceAll={handleBackspaceAll}
            onEndCall={handleEndCall}
            onRejectCall={handleRejectCall}
            onAcceptCall={handleAcceptCall}
            environmentS={environmentS}
          />

          <S.AudioElement id="remoteAudio" autoPlay />
        </S.Container>
      </S.PageContainer>
      {callStatus !== 'ended' && callStatus !== 'idle' && (
        <S.ToggleButtonWrapper $isVisible={callIsVisibleS} $pcEnvironment={pcEnvironment}>
          <S.ToggleButton
            onClick={() => {
              // 버튼 클릭 시 opacity 토글하는 동작을 여기서 처리
              setCallIsVisibleS((prev) => !prev);
            }}
          >
            {!callIsVisibleS ? <TiArrowSortedDown size={22} /> : <TiArrowSortedUp size={22} />}
          </S.ToggleButton>
        </S.ToggleButtonWrapper>
      )}
    </div>
  );
};

export default SipWebClient;
